import "./SelectDropdownComponent.scss";
import React, {useCallback, useEffect, useState} from 'react';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import {FormHelperText} from "@mui/material";
import CommonService from "../../../services/common.service";
import ButtonComponent from "../button/ButtonComponent";
import {CloseRounded} from "@mui/icons-material";

export interface SelectDropdownComponentProps {
    className?: string;
    name: string;
    label?: string;
    value?: any;
    sx?: any;
    placeholder?: string;
    variant?: "filled" | "standard" | "outlined" | undefined;
    size?: "small" | "medium" | undefined;
    color?: "error" | "primary" | "secondary" | "info" | "success" | "warning" | undefined;
    required?: boolean;
    options: Array<any>;
    isDataLoading?: boolean;
    isDataLoadingFailed?: boolean;
    isDataLoaded?: boolean;
    valueKey?: string;
    getOptionLabel?: (option: any) => string;
    onUpdate?: Function;
    multiple?: boolean;
    id?: any;
    hasError?: boolean;
    errorMessage?: any;
    defaultValue?: any;
    onBlur?: () => void;
    disabled?: boolean;
    readOnly?: boolean;
}

const SelectDropdownComponent = (props: SelectDropdownComponentProps) => {
    const [formValue, setFormValue] = useState<any | undefined>(props.value || "");

    const {
        className='',
        label,
        name,
        id,
        options,
        onUpdate,
        isDataLoading,
        isDataLoaded,
        isDataLoadingFailed,
        defaultValue,
        required,
        hasError,
        size,
        placeholder,
        errorMessage,
        onBlur,
        // value,
    } = props;

    useEffect(() => {
        setFormValue(props.value);
    }, [props.value]);

    let {variant, readOnly, multiple, sx, getOptionLabel, color, valueKey, disabled} = props;
    if (!variant) variant = "outlined";
    if (!multiple) multiple = false;
    if (!color) color = "secondary";
    if (!disabled) disabled = false;
    if (!getOptionLabel) getOptionLabel = (option => option.name ? option.name : option);
    if (!readOnly) readOnly = false;

    const handleClear = useCallback(() => {
        if (onUpdate) {
            multiple ? onUpdate([]) : onUpdate('');
        }
        setFormValue(multiple ? [] : '');
    }, [onUpdate, multiple]);

    const ClearOption = useCallback(() => (
        <ButtonComponent variant={'icon'}
                         size={"small"}
                         color={'secondary'}
                         data-testid={'clear-button'}
                         handleClick={() => handleClear()}
                         className={'mrg-right-15'}
        >
            <CloseRounded fontSize={'small'}/>
        </ButtonComponent>

    ), [handleClear]);

    return (
        <FormControl fullWidth sx={sx} variant={variant} error={hasError} color={color}
                     className={`select-component-wrapper ${className}`}>
            <InputLabel>
                <div className={'select-label-wrapper'}>
                    <div className={'select-label'}>
                        {label}
                    </div>
                    {
                        required && <div className={'required-asterisk'}>
                            *
                        </div>
                    }
                </div>
            </InputLabel>
            <Select
                id={id}
                name={name}
                label={label}
                required={required}
                multiple={multiple}
                value={formValue ?? ""}
                disabled={disabled}
                size={size}
                readOnly={readOnly}
                placeholder={placeholder}
                defaultValue={defaultValue}
                onKeyDown={e => e.preventDefault()}
                onBlur={onBlur}
                sx={{
                    "& .MuiOutlinedInput-root": {
                        background: "#FAFAFA"
                    }
                }
                }
                onChange={(event) => {
                    setFormValue(event.target.value);
                    if (onUpdate) {
                        onUpdate(event.target.value);
                    }
                }}
                endAdornment={(formValue && formValue !== '') && !readOnly && <ClearOption/>}
            >
                {
                    (options && options?.length) > 0 ? options?.map((option) => (
                            <MenuItem disabled={option?.disabled} value={valueKey ? option[valueKey] : option}
                                      key={CommonService.getUUID()}>
                                {getOptionLabel && getOptionLabel(option)}
                            </MenuItem>
                        )) :
                        <div className={'select-dropdown-no-data-message'}>
                            No Data
                        </div>
                }
            </Select>
            <FormHelperText>
                {hasError && <div id={id + '_error'}> {errorMessage} </div>}
                {isDataLoading && !hasError && <> Data is loading </>}
                {isDataLoadingFailed && !hasError && <> Data failed to load </>}
                {(isDataLoaded && options?.length === 0) && <> Data is
                    empty </>}
            </FormHelperText>
        </FormControl>
    );

};

export default SelectDropdownComponent;
